import { map } from 'lodash';
import { PropTypes } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getFormSyncErrors, submit } from 'redux-form';
import { hideDialog } from '../../../core/actions';
import { checkErrors } from '../../../core/formValidation';
import * as actions from '../actions';
import DetergentEditForm from '../components/DetergentEditForm';
import { selectDetergentInEdit } from '../reducer';
import DetergentModalDialog from '../../../core/components/DetergentModal';
import { getApiStatus } from '../../../core/utils';
import { GET_DETERGENT_TEMP } from '../actions';
import Loader from '../../../core/components/Loader';

const getErrors = getFormSyncErrors('detergent');

const EditDetergentDialog = (props) => {
  const { isOpen, title, detergentContainerId, addNew, products, inventory, detergents } = props;

  const dispatch = useDispatch();
  const initialDetergent = useSelector(selectDetergentInEdit);
  const errors = useSelector((state) => getErrors(state));
  const isDetergentsLoading = useSelector((state) => getApiStatus(state, GET_DETERGENT_TEMP));

  const [selectedDetergent, setSelectedDetergent] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedInventory, setSelectedInventory] = useState(null);

  // Handle initialDetergent changes (equivalent to componentDidUpdate)
  useEffect(() => {
    if (initialDetergent) {
      setSelectedDetergent({
        value: initialDetergent?.detergentId || '',
        label: initialDetergent?.detergent.name || '',
      });
      setSelectedProducts(
        map(initialDetergent?.products, (product) => ({
          value: product.id,
          label: product.name,
        }))
      );
      setSelectedInventory(
        initialDetergent?.inventory
          ? {
              value: initialDetergent.inventory.id,
              label: initialDetergent.inventory.name,
            }
          : null
      );
    }
  }, [initialDetergent]);

  // Cleanup on unmount (equivalent to componentWillUnmount)
  useEffect(() => {
    return () => {
      dispatch(actions.clearDetergentSelections());
    };
  }, [dispatch]);

  const onReady = () => {
    dispatch(submit('detergent'));
  };

  const onSubmit = (values) => {
    // add default values
    if (!values.capacity) {
      values.capacity = '0';
    }
    if (!values.alarmLevel) {
      values.alarmLevel = '0';
    }
    if (!values.reorderLevel) {
      values.reorderLevel = '0';
    }
    const formValues = {
      ...values,
      alarmLevel: `${values.alarmLevel}`,
      capacity: `${values.capacity}`,
      reorderLevel: `${values.reorderLevel}`,
      detergentId: selectedDetergent ? parseInt(selectedDetergent.value, 10) : null,
      products: map(selectedProducts, (product) => ({ id: product.value })),
      inventoryItem: selectedInventory ? selectedInventory.value : null,
      detergentContainerId: detergentContainerId,
    };
    dispatch(actions.upsertDetergentContainer(formValues));
    dispatch(hideDialog());
  };

  const handleDetergentChange = (newSelectedDetergent) => {
    setSelectedDetergent(newSelectedDetergent);
  };

  const handleProductChange = (newSelectedProducts) => {
    setSelectedProducts(newSelectedProducts);
  };

  const handleInventoryChange = (newSelectedInventory) => {
    setSelectedInventory(newSelectedInventory);
  };

  const handleSelectAllProducts = () => {
    if (products && products.length > 0) {
      const allProducts = products.map((product) => ({
        value: product.id,
        label: product.name,
      }));
      handleProductChange(allProducts);
    }
  };

  const submitDisabled = checkErrors(errors);

  // Mutate initialDetergent for form (same as before)
  const formInitialValues = initialDetergent
    ? {
        ...initialDetergent,
        inventoryItem: initialDetergent.inventory ? initialDetergent.inventory.id : -1,
      }
    : initialDetergent;

  return (
    <DetergentModalDialog
      className="modal-dialog enhanced-dialog medium padded"
      title={title}
      onReady={onReady}
      isOpen={isOpen}
      close={() => dispatch(hideDialog())}
      disabled={submitDisabled || isDetergentsLoading}
    >
      {isDetergentsLoading ? (
        <Loader />
      ) : (
        <DetergentEditForm
          onSubmit={onSubmit}
          initialValues={formInitialValues}
          products={products}
          inventory={inventory}
          detergents={detergents}
          selectedDetergent={selectedDetergent ?? []}
          handleDetergentChange={handleDetergentChange}
          handleProductChange={handleProductChange}
          handleInventoryChange={handleInventoryChange}
          selectedInventory={selectedInventory}
          addNew={addNew}
          selectedProducts={selectedProducts}
          handleSelectAllProducts={handleSelectAllProducts}
          enableReinitialize
        />
      )}
    </DetergentModalDialog>
  );
};

EditDetergentDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string,
  detergentContainerId: PropTypes.number,
  addNew: PropTypes.bool,
  products: PropTypes.arrayOf(PropTypes.shape({})),
  inventory: PropTypes.arrayOf(PropTypes.shape({})),
  detergents: PropTypes.arrayOf(PropTypes.shape({})),
};

export default EditDetergentDialog;
